import { cn } from '@/lib/utils';
import { AlertTriangle, RotateCcw } from 'lucide-react';
import { Component, type ReactNode } from 'react';
import { i18n } from '@tonnie/locale';

interface ErrorBoundaryProps {
  children: ReactNode;
  variant?: 'page' | 'section';
  title?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Detect the "browser translator mutated the DOM under React" error.
 * Primary defence is `shared/translation-shield.ts` (installed at app
 * startup, patches `Node.prototype.insertBefore`/`removeChild`).
 * This is the belt-and-braces second layer — if the shield missed a
 * case we self-heal here instead of showing the crash screen.
 * See journey-web's ErrorBoundary for the full rationale.
 */
function isTranslatorDomError(error: Error | null): boolean {
  if (!error) return false;
  if (error.name !== 'NotFoundError') return false;
  const msg = error.message || '';
  return /insertBefore|removeChild/.test(msg) && /not a child of this node/.test(msg);
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  // One silent auto-recovery, then fall through to the normal UI to
  // avoid an infinite reload loop if something else is going wrong.
  private translatorRecoveryCount = 0;
  private translatorRecoveryTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentWillUnmount(): void {
    if (this.translatorRecoveryTimer) {
      clearTimeout(this.translatorRecoveryTimer);
    }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('[ErrorBoundary]', error, errorInfo);

    // Translator-induced DOM error: self-heal silently. The shield at
    // startup is supposed to prevent this ever bubbling, but if a new
    // browser/extension slips past, one clean remount is far better
    // UX than the red error screen.
    if (isTranslatorDomError(error) && this.translatorRecoveryCount === 0) {
      this.translatorRecoveryCount++;
      this.translatorRecoveryTimer = setTimeout(() => {
        this.setState({ hasError: false, error: null });
      }, 50);
    }
  }

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    const { variant = 'page', title = i18n.t('errorBoundary.somethingWentWrong') } = this.props;

    return (
      <div
        data-testid="app-error-fallback"
        data-variant={variant}
        className={cn(
          'flex items-center justify-center p-8 bg-background',
          variant === 'page' && 'min-h-screen'
        )}
      >
        <div className="flex flex-col items-center w-full max-w-2xl p-8">
          <AlertTriangle size={48} className="text-destructive-accessible mb-6 shrink-0" />
          <h2 className="text-xl mb-4">{title}</h2>

          {import.meta.env.DEV && this.state.error && (
            <div className="p-4 w-full rounded bg-muted overflow-auto mb-6">
              <pre className="text-sm text-muted-foreground whitespace-break-spaces">
                {this.state.error.stack}
              </pre>
            </div>
          )}

          <button
            onClick={() => window.location.reload()}
            className={cn(
              'flex items-center gap-2 px-4 py-2 rounded-lg',
              'bg-primary text-primary-foreground',
              'hover:opacity-90 cursor-pointer'
            )}
          >
            <RotateCcw size={16} />
            {i18n.t('errorBoundary.reloadPage')}
          </button>
        </div>
      </div>
    );
  }
}

export function PageErrorBoundary({ children }: { children: ReactNode }) {
  return (
    <ErrorBoundary variant="section" title={i18n.t('errorBoundary.pageEncounteredError')}>
      {children}
    </ErrorBoundary>
  );
}

export default ErrorBoundary;
